import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const serif = { fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif" };
const pad3 = (n) => `#${String(n).padStart(3, "0")}`;

/**
 * FoundersChoiceGate — shown once per session to logged-in Founding Members
 * who have not yet chosen their public Founders Wall display preference.
 * Existing members receive this choice on their next login, per spec.
 */
export default function FoundersChoiceGate() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const [me, setMe] = useState(null);
  const [choice, setChoice] = useState("yes");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user) { setOpen(false); return; }
    if (sessionStorage.getItem("fw-choice-deferred")) return;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/founders/me`, { credentials: "include" });
        if (!res.ok) return;
        const json = await res.json();
        if (alive && json.is_founder && json.display !== "yes" && json.display !== "no") {
          setMe(json);
          setOpen(true);
        }
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user, loading]);

  if (!open || !me) return null;

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/founders/me`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display: choice }),
      });
      if (res.ok) setOpen(false);
    } catch (_) { /* keep modal open */ }
    setBusy(false);
  };

  const defer = () => {
    sessionStorage.setItem("fw-choice-deferred", "1");
    setOpen(false);
  };

  return (
    <div
      data-testid="founders-choice-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9500] flex items-center justify-center px-4"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" aria-hidden />
      <div className="relative z-10 w-full max-w-lg bg-[#0a0a0a] border border-gold/30 shadow-2xl p-7 md:p-10">
        <button
          type="button"
          onClick={defer}
          data-testid="founders-choice-close"
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-bone/60 hover:text-gold transition-colors"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <p className="text-[10px] tracking-[0.32em] uppercase text-gold mb-3">{t("fwModal.eyebrow")}</p>
        <h2 style={serif} className="text-2xl md:text-3xl text-bone leading-tight">
          {t("fwModal.title")}
        </h2>
        <p className="mt-4 text-sm text-bone/65 leading-relaxed">
          {t("fwModal.body")}
        </p>

        <div className="mt-7 space-y-3">
          <label
            data-testid="founders-choice-yes"
            className={`flex items-start gap-3 cursor-pointer border px-4 py-3.5 transition-colors ${choice === "yes" ? "border-gold bg-gold/10" : "border-white/12 hover:border-gold/40"}`}
          >
            <input type="radio" name="fw-choice" checked={choice === "yes"} onChange={() => setChoice("yes")} className="mt-1 accent-[#C5A059]" />
            <span className="text-sm text-bone/85 leading-relaxed">
              {t("join.fw.yes")}
              {me.display_preview ? <span className="block mt-1 text-gold/80 text-xs">→ {me.display_preview}</span> : null}
            </span>
          </label>
          <label
            data-testid="founders-choice-no"
            className={`flex items-start gap-3 cursor-pointer border px-4 py-3.5 transition-colors ${choice === "no" ? "border-gold bg-gold/10" : "border-white/12 hover:border-gold/40"}`}
          >
            <input type="radio" name="fw-choice" checked={choice === "no"} onChange={() => setChoice("no")} className="mt-1 accent-[#C5A059]" />
            <span className="text-sm text-bone/85 leading-relaxed">{t("join.fw.no")}</span>
          </label>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={defer}
            data-testid="founders-choice-later"
            className="text-[11px] tracking-[0.2em] uppercase text-bone/40 hover:text-gold transition-colors"
          >
            {t("fwModal.later")}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="founders-choice-save"
            className="min-h-[44px] px-6 py-3 bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-gold/90 disabled:opacity-60 transition-colors"
          >
            {busy ? "…" : t("fwModal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
